import { t } from 'i18next';
import { mapValues } from 'lodash';
import { useCallback, useSyncExternalStore } from 'react';
import { InfiniteData, QueryCache, useMutation, useQuery, useQueryClient } from 'react-query';

import { ICliJob } from 'cli/types';
import { NotificationPosition } from 'core/enums';
import { useCliJobHandler, useHandleErrors, useInfiniteQuery } from 'core/hooks';
import { useNotificationContext, useSelectionCountContext } from 'core/states';
import { GetManyResponse, IInfiniteQueryOptions } from 'core/types';
import { partService } from 'parts/services';
import { TradeAgreementUnavailableReason } from 'quotes/enums';
import { QuotePartHelper } from 'quotes/helpers';
import { quotePartService } from 'quotes/services';
import {
  ICustomerQuotePart,
  ICustomerQuotePartListRequestPayload,
  IQuotePartActionOptions,
  IQuotePartExtraData,
  IRemoveQuotePartNotesByTypePayload,
  ITradeAgreementLine,
  IVendorQuotePart,
  IVendorQuotePartListRequestPayload,
  TTradeAgreementsRequest,
} from 'quotes/types';

const CUSTOMER_QUOTE_PARTS_QUERY_KEY = 'customerQuoteParts';

export const useGetCustomerQuoteParts = (
  requestPayload?: ICustomerQuotePartListRequestPayload,
  options: IInfiniteQueryOptions<ICustomerQuotePart> = {}
) =>
  useInfiniteQuery<ICustomerQuotePart>(
    quotePartService.getCustomerQuoteParts,
    CUSTOMER_QUOTE_PARTS_QUERY_KEY,
    requestPayload,
    options
  );

type TCustomerQuotePartsData = InfiniteData<
  GetManyResponse<ICustomerQuotePart, IQuotePartExtraData>
>;

// The newest loaded page of any version of the quote's grid: each read derives the window anew.
const getLatestQuotePartListExtraData = (
  queryCache: QueryCache,
  quoteId: number
): IQuotePartExtraData | null => {
  let latest: IQuotePartExtraData | null = null;
  let latestUpdatedAt = -1;

  queryCache.findAll([CUSTOMER_QUOTE_PARTS_QUERY_KEY]).forEach(({ queryKey, state }) => {
    const requestPayload = queryKey?.[1] as ICustomerQuotePartListRequestPayload | undefined;
    const pages = (state.data as TCustomerQuotePartsData | undefined)?.pages;

    if (
      requestPayload?.quoteId !== quoteId ||
      !pages?.length ||
      state.dataUpdatedAt <= latestUpdatedAt
    ) {
      return;
    }

    latest = pages[pages.length - 1]?.extraData ?? null;
    latestUpdatedAt = state.dataUpdatedAt;
  });

  return latest;
};

/**
 * Reads the grid's cached parts answer and never fetches: a form opened away from the grid knows
 * nothing and lets the BE refuse.
 */
export const useQuotePricingWindowMessage = (quoteId?: number | null): string | null => {
  const queryCache = useQueryClient().getQueryCache();

  const subscribe = useCallback(
    (onStoreChange: () => void) => queryCache.subscribe(() => onStoreChange()),
    [queryCache]
  );

  const extraData = useSyncExternalStore(subscribe, () =>
    quoteId ? getLatestQuotePartListExtraData(queryCache, quoteId) : null
  );

  return QuotePartHelper.quotePricingWindowMessage(extraData);
};

export const useGetVendorQuoteParts = (
  requestPayload?: IVendorQuotePartListRequestPayload,
  options: { enabled?: boolean; fetchAllPages?: boolean } = {}
) =>
  useInfiniteQuery<IVendorQuotePart>(
    quotePartService.getVendorQuoteParts,
    'vendorQuoteParts',
    requestPayload,
    options
  );

export const useGetQuotePartPricingGuidance = (quotePartId: number, enabled = true) => {
  const { data, isError, isLoading, isFetching } = useQuery(
    ['quotePartPricingGuidance', quotePartId],
    ({ signal }) => quotePartService.getQuotePartPricingGuidance(quotePartId, signal),
    {
      keepPreviousData: true,
      enabled,
    }
  );

  return {
    records: data?.records ?? [],
    isError,
    isLoading,
    isFetching,
  };
};

const getTradeAgreementsQueryKey = (requestPayload: TTradeAgreementsRequest) => [
  'quotePartTradeAgreements',
  requestPayload,
];

// No keepPreviousData: it would hand back the previous line's agreements, selectable, mid-flight.
export const useGetTradeAgreements = (requestPayload: TTradeAgreementsRequest, enabled = false) => {
  const {
    data,
    dataUpdatedAt,
    error,
    errorUpdatedAt,
    isError,
    isFetching,
    isLoading,
    isSuccess,
    refetch,
  } = useQuery(
    getTradeAgreementsQueryKey(requestPayload),
    ({ signal }) => quotePartService.getTradeAgreements(requestPayload, signal),
    // A 422 or 5xx shows at once, not after three more calls the BE may hold up to 30 s each.
    { enabled, retry: false }
  );

  return {
    lines: data?.records ?? [],
    dataUpdatedAt,
    error,
    errorUpdatedAt,
    isError,
    isFetching,
    isLoading,
    isSuccess,
    refetch,
  };
};

// Under the drawer's own key, so the drawer and the card start from this answer.
export const useFetchTradeAgreements = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (requestPayload: TTradeAgreementsRequest) =>
      queryClient.fetchQuery(getTradeAgreementsQueryKey(requestPayload), ({ signal }) =>
        quotePartService.getTradeAgreements(requestPayload, signal)
      ),
    [queryClient]
  );
};

// React Query caches a failure (a `line_error` 200 too); dropped, the next reader loads afresh.
// One being loaded stays: removing it would cancel that request.
export const useDropFailedTradeAgreements = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (requestPayload: TTradeAgreementsRequest) => {
      const queryKey = getTradeAgreementsQueryKey(requestPayload);
      const state = queryClient.getQueryState<GetManyResponse<ITradeAgreementLine>>(queryKey);

      if (!state || state.isFetching) {
        return;
      }

      const hasFailed =
        'error' === state.status ||
        !!state.data?.records?.some(
          ({ reason }) => TradeAgreementUnavailableReason.LINE_ERROR === reason
        );

      if (hasFailed) {
        queryClient.removeQueries(queryKey, { exact: true });
      }
    },
    [queryClient]
  );
};

export const useQuotePartActions = ({ notificationPosition }: IQuotePartActionOptions = {}) => {
  const { selectionCount } = useSelectionCountContext();

  return {
    ...useReorderQuoteParts(),
    ...useUpsertQuotePart(notificationPosition),
    ...useChangeQuotePart(notificationPosition),
    ...useUpsertQuoteParts(selectionCount, notificationPosition),
    ...useUpsertQuotePartNotes(selectionCount),
    ...useUpsertQuotePartNote(),
    ...useRemoveQuotePartNote(),
    ...useRemoveQuotePartNotesByType(selectionCount),
    ...useRemoveQuoteParts(selectionCount),
    ...useResetPricing(notificationPosition),
    ...useResetExpiredCosting(),
    ...useMoveTo(selectionCount),
    ...useDuplicate(selectionCount),
    ...useApplyCostDiscount(),
    ...useCalculatePrices(notificationPosition),
    ...useAddToPricingProfileForm(selectionCount),
    ...useGetDiscountPartFixed(),
    ...useAddPnbVariations(),
    ...useSetPriceBySource(),
  };
};

const useReorderQuoteParts = () => {
  const { handleCliJob } = useCliJobHandler();
  const { showErrorNotification, showSuccessNotification } = useNotificationContext();
  const queryClient = useQueryClient();

  const { errors } = useHandleErrors({
    error: { title: t('errorTitleLinesReorder') },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync, isLoading } = useMutation(
    (data) => quotePartService.upsertQuotePartsAsync(data),
    {
      ...errors,
    }
  );

  const reorderQuoteParts = (data, options: { onSuccess?: () => void } = {}) => {
    return new Promise(
      (resolve: (value: { success: boolean; quoteParts: ICustomerQuotePart[] }) => void) => {
        handleCliJob(mutateAsync, data, {
          cliJobInProgressMessage: t('messageLinesReorderInProgress'),
          onCliJobFinished: (cliJob: ICliJob) => {
            const { quoteParts } = cliJob.result as Record<string, ICustomerQuotePart[]>;

            showSuccessNotification(
              { message: t('messageLinesReorderSuccess') },
              NotificationPosition.TOP
            );

            options.onSuccess?.();

            resolve({
              success: true,
              quoteParts,
            });
          },
          onCliJobStopped: (cliJob: ICliJob) => {
            showErrorNotification(
              {
                title: t('errorTitleLinesReorder'),
                message: t('messageCliJobInterrupted', {
                  description: cliJob.statusDescription || cliJob.status,
                }),
              },
              NotificationPosition.TOP
            );

            resolve({
              success: false,
              quoteParts: [],
            });
          },
          // Settles the promise so the grid re-enables row dragging; the refetch shows the saved
          // order.
          onCliJobPollFailed: ({ showLostTrackNotice }) => {
            queryClient.invalidateQueries(CUSTOMER_QUOTE_PARTS_QUERY_KEY);
            showLostTrackNotice();
            resolve({ success: false, quoteParts: [] });
          },
        });
      }
    );
  };

  return { reorderQuoteParts, isLoading };
};

const useUpsertQuotePart = (position = NotificationPosition.TOP) => {
  const { shouldUpdateUnverifiedPart, handleUnverifiedPartUpdate } = quotePartUpsertHelper();

  const { errors } = useHandleErrors({
    success: ({ quotePart }) =>
      quotePart.id
        ? { message: t('messageLinesUpdateSuccess', { count: 1 }) }
        : { message: t('messageLineCreatingSuccess') },
    loading: ({ quotePart }) =>
      quotePart.id
        ? { message: t('messageLinesUpdateInProgress', { count: 1 }) }
        : { message: t('messageLineCreatingInProgress') },
    error: ({ quotePart }) => ({
      title: quotePart.id ? t('errorTitleLinesUpdate', { count: 1 }) : t('errorTitleLineCreating'),
      // In the drawer the line stays open on failure, so keep the reason visible (see
      // useChangeQuotePart); list surfaces (TOP) keep the default auto-close behavior.
      disableAutoClose: NotificationPosition.DRAWER === position,
    }),
    position,
    invalidateQueries: ['quoteVersionData'],
  });

  const { mutateAsync, isLoading } = useMutation(
    async (data) => {
      if (shouldUpdateUnverifiedPart(data)) {
        return handleUnverifiedPartUpdate(data);
      }

      return quotePartService.upsertQuotePart(data);
    },
    {
      ...errors,
    }
  );

  return { upsertQuotePart: mutateAsync, isLoading };
};

const useChangeQuotePart = (position = NotificationPosition.DRAWER) => {
  const { errors } = useHandleErrors({
    success: { message: t('messageLineChangeSuccess') },
    loading: { message: t('messageLineChangeInProgress') },
    // In the drawer the line stays open on failure (e.g. "Part has been already added") with
    // no other feedback, so keep the error visible until the user acts; list surfaces (TOP)
    // keep the default auto-close behavior (mirrors useUpsertQuotePart).
    error: {
      title: t('errorTitleLineChange'),
      disableAutoClose: NotificationPosition.DRAWER === position,
    },
    position,
    invalidateQueries: ['quoteVersionData'],
  });

  const { mutateAsync } = useMutation((data) => quotePartService.changeQuotePart(data), {
    ...errors,
  });

  return { changeQuotePart: mutateAsync };
};

interface IUpsertQuotePartsResult {
  // The other lines the save changed, to re-read.
  affectedQuotePartIds: number[];
  quoteParts: ICustomerQuotePart[];
  success: boolean;
}

const useUpsertQuoteParts = (selectionCount: number, position = NotificationPosition.TOP) => {
  const { handleCliJob } = useCliJobHandler();
  const { showErrorNotification, showSuccessNotification } = useNotificationContext();
  const queryClient = useQueryClient();

  const { errors } = useHandleErrors({
    error: { title: t('errorTitleQuotePartsUpdate', { count: selectionCount }) },
    position,
    invalidateQueries: ['quoteVersionData'],
  });

  const { mutateAsync, isLoading } = useMutation(
    (data) => quotePartService.upsertQuotePartsAsync(data, { includeNullValues: true }),
    {
      ...errors,
    }
  );

  const upsertQuoteParts = (data, options: { onSuccess?: () => void } = {}) => {
    return new Promise((resolve: (value: IUpsertQuotePartsResult) => void) => {
      handleCliJob(mutateAsync, data, {
        cliJobInProgressMessage: t('messageLinesUpdateInProgress', {
          count: selectionCount,
        }),
        onCliJobFinished: (cliJob: ICliJob) => {
          const { affectedQuotePartIds, quoteParts } = cliJob.result as Omit<
            IUpsertQuotePartsResult,
            'success'
          >;

          showSuccessNotification(
            { message: t('messageLinesUpdateSuccess', { count: selectionCount }) },
            NotificationPosition.TOP
          );

          options.onSuccess?.();

          resolve({
            affectedQuotePartIds: affectedQuotePartIds ?? [],
            success: true,
            quoteParts,
          });
        },
        // Without it the promise never settles and every caller's `if (!success)` is unreachable.
        onCliJobStopped: (cliJob: ICliJob) => {
          showErrorNotification(
            {
              title: t('errorTitleQuotePartsUpdate', { count: selectionCount }),
              message: t('messageCliJobInterrupted', {
                description: cliJob.statusDescription || cliJob.status,
              }),
            },
            NotificationPosition.TOP
          );

          resolve({
            affectedQuotePartIds: [],
            success: false,
            quoteParts: [],
          });
        },
        onCliJobPollFailed: ({ showLostTrackNotice }) => {
          queryClient.invalidateQueries(CUSTOMER_QUOTE_PARTS_QUERY_KEY);
          showLostTrackNotice();
          resolve({ affectedQuotePartIds: [], success: false, quoteParts: [] });
        },
      });
    });
  };

  return { upsertQuoteParts, isLoading };
};

const useUpsertQuotePartNotes = (selectionCount: number) => {
  const { errors } = useHandleErrors({
    error: { title: t('errorTitleLineNotesCreate', { count: selectionCount }) },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation((data) => quotePartService.upsertQuotePartNotes(data), {
    ...errors,
  });

  const upsertQuotePartNotes = (data) => mutateAsync(data);

  return { upsertQuotePartNotes };
};

const useUpsertQuotePartNote = () => {
  const { errors } = useHandleErrors({
    success: ({ quotePartNote }) => {
      return {
        message: quotePartNote?.id
          ? t('messageLineNoteUpdateSuccess')
          : t('messageLineNoteCreateSuccess'),
      };
    },
    loading: ({ quotePartNote }) => {
      return {
        message: quotePartNote?.id
          ? t('messageLineNoteUpdateInProgress')
          : t('messageLineNoteCreateInProgress'),
      };
    },
    error: ({ quotePartNote }) => {
      return {
        title: quotePartNote?.id ? t('errorTitleLineNoteUpdate') : t('errorTitleLineNoteCreate'),
      };
    },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation((data) => quotePartService.upsertQuotePartNote(data), {
    ...errors,
  });

  return { upsertQuotePartNote: mutateAsync };
};

const useRemoveQuotePartNotesByType = (selectionCount: number) => {
  const { errors } = useHandleErrors({
    error: { title: t('errorTitleLineNotesRemove', { count: selectionCount }) },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation(
    (data: IRemoveQuotePartNotesByTypePayload) => quotePartService.removeQuotePartNotesByType(data),
    {
      ...errors,
    }
  );

  return { removeQuotePartNotesByType: mutateAsync };
};

const useRemoveQuotePartNote = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageLineNoteRemoveSuccess') },
    loading: { message: t('messageLineNoteRemoveInProgress') },
    error: { title: t('errorTitleLineNoteRemove') },
    position: NotificationPosition.DRAWER,
  });

  const { mutateAsync } = useMutation((data) => quotePartService.removeQuotePartNote(data), {
    ...errors,
  });

  return { removeQuotePartNote: mutateAsync };
};

const useRemoveQuoteParts = (selectionCount: number) => {
  const { errors } = useHandleErrors({
    error: { title: t('errorTitleLinesRemove', { count: selectionCount }) },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation((data) => quotePartService.removeQuoteParts(data), {
    ...errors,
  });

  const removeQuoteParts = (data) => mutateAsync(data);

  return { removeQuoteParts };
};

const useResetPricing = (position = NotificationPosition.FORM) => {
  const { errors } = useHandleErrors({
    error: (data) => ({
      title: t('errorTitleResetPricingType', {
        type: getPricingTypeLabels(data?.pricingTypes),
      }),
    }),
    position,
  });

  const { mutateAsync } = useMutation((data) => quotePartService.resetPricing(data), {
    ...errors,
  });

  const resetPricing = (data) => mutateAsync(data);

  return { resetPricing };
};

const useResetExpiredCosting = () => {
  const { errors } = useHandleErrors({
    loading: { message: t('messageExpiredCostingResetInProgress') },
    error: { title: t('errorTitleExpiredCostingReset') },
    position: NotificationPosition.TOP,
    doNotClearNotification: true,
  });

  const { mutateAsync } = useMutation((data) => quotePartService.resetExpiredCosting(data), {
    ...errors,
  });

  const resetExpiredCosting = (data) => mutateAsync(data);

  return { resetExpiredCosting };
};

const useMoveTo = (selectionCount: number) => {
  const { errors } = useHandleErrors({
    error: {
      title: t('errorTitleLinesMoveTo', { count: selectionCount }),
    },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation((data) => quotePartService.moveTo(data), {
    ...errors,
  });

  const moveTo = (data) => mutateAsync(data);

  return { moveTo };
};

const useDuplicate = (selectionCount: number) => {
  const { errors } = useHandleErrors({
    error: {
      title: t('errorTitleLineDuplicate', { count: selectionCount }),
    },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation((data) => quotePartService.duplicate(data), {
    ...errors,
  });

  return { duplicate: mutateAsync };
};

const useApplyCostDiscount = () => {
  const { errors } = useHandleErrors({
    error: { title: t('errorApplyCostDiscount') },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation((data) => quotePartService.applyCostDiscount(data), {
    ...errors,
  });

  const applyCostDiscount = (data) => mutateAsync(data);

  return { applyCostDiscount };
};

export const useCalculatePrices = (notificationPosition = NotificationPosition.FORM) => {
  const { errors } = useHandleErrors({
    loading: { message: t('messageCalculatePricesInProgress') },
    error: { title: t('errorTitleCalculatePrices') },
    position: notificationPosition,
  });

  const { mutateAsync } = useMutation((data) => quotePartService.calculatePrices(data), {
    ...errors,
  });

  return { calculatePrices: mutateAsync };
};

const useAddToPricingProfileForm = (selectionCount: number) => {
  const { errors } = useHandleErrors({
    error: { title: t('errorTitleAddToPricingProfile', { count: selectionCount }) },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation((data) => quotePartService.addToPricingProfile(data), {
    ...errors,
  });

  const addToPricingProfile = (data) => mutateAsync(data);

  return { addToPricingProfile };
};

const useGetDiscountPartFixed = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageDiscountedPartsFetchSuccess') },
    loading: { message: t('messageDiscountedPartsFetchInProgress') },
    error: { title: t('errorDiscountedPartsFetch') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation((data) => quotePartService.getDiscountPartsFixed(data), {
    ...errors,
  });

  const fetchDiscountedParts = (data) => mutateAsync(data);

  return { fetchDiscountedParts };
};

export const getPricingTypeLabels = (pricingTypes: string[]) => {
  let type = '';
  if (pricingTypes.includes('cost')) {
    type = 'cost';
  }

  if (pricingTypes.includes('price')) {
    type += type ? ' and price' : 'price';
  }

  return type;
};

const useAddPnbVariations = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageAddPnbVariationsSuccess') },
    loading: { message: t('messageAddPnbVariationsInProgress') },
    error: { title: t('errorTitleAddPnbVariations') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation((data) => quotePartService.addPnbVariations(data), {
    ...errors,
  });

  return { addPnbVariations: mutateAsync };
};

const useSetPriceBySource = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageLinesUpdateSuccess', { count: 1 }) },
    loading: { message: t('messageLinesUpdateInProgress', { count: 1 }) },
    error: { title: t('errorTitleLinesUpdate', { count: 1 }) },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation((data) => quotePartService.setPriceBySource(data), {
    ...errors,
  });

  return { setPriceBySource: mutateAsync };
};

const quotePartUpsertHelper = () => {
  const IDENTITY_FIELDS = ['id', 'partId'];
  const PART_FIELDS = ['description'];

  const partToJobPartFieldsMap = {
    description: 'description',
    descriptionLong: 'description',
  };

  // If quotePart contains only part fields, upsert the part instead, not the quotePart
  const shouldUpdateUnverifiedPart = (data) => {
    const fieldsWithoutIdentity = Object.keys(data.quotePart).filter(
      (field) => !IDENTITY_FIELDS.includes(field)
    );

    return (
      data.quotePart.partId &&
      fieldsWithoutIdentity.length > 0 &&
      fieldsWithoutIdentity.every((field) => PART_FIELDS.includes(field))
    );
  };

  const createUnverifiedPartPayload = (data) => ({
    part: {
      id: data.quotePart.partId,
      ...mapValues(partToJobPartFieldsMap, (value) => data.quotePart?.[value]),
    },
  });

  const handleUnverifiedPartUpdate = async (data) => {
    const { part, ...rest } = await partService.upsertUnverifiedPart(
      createUnverifiedPartPayload(data)
    );

    return {
      quotePart: { partArr: part },
      ...rest,
    };
  };

  return {
    shouldUpdateUnverifiedPart,
    handleUnverifiedPartUpdate,
  };
};
